import { computed, inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, withComputed } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, withStorageSync, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { IImageUploadProps } from 'src/models/common.model';
import { IAccountState } from 'src/models/account.model';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';

enum EAccountActions {
  MAX_IMAGE_SIZE_REQUEST = '[Account] Max Image Size Request',
  MAX_IMAGE_SIZE_SUCCESS = '[Account] Max Image Size Success',
  MAX_IMAGE_SIZE_FAILURE = '[Account] Max Image Size Failure',

  USER_PHOTO_REQUEST = '[Account] User Photo Request',
  USER_PHOTO_SUCCESS = '[Account] User Photo Success',
  USER_PHOTO_FAILURE = '[Account] User Photo Failure',

  UPDATE_USER_PHOTO_REQUEST = '[Account] Update User Photo Request',
  UPDATE_USER_PHOTO_SUCCESS = '[Account] Update User Photo Success',
  UPDATE_USER_PHOTO_FAILURE = '[Account] Update User Photo Failure',

  DELETE_USER_PHOTO_REQUEST = '[Account] Delete User Photo Request',
  DELETE_USER_PHOTO_SUCCESS = '[Account] Delete User Photo Success',
  DELETE_USER_PHOTO_FAILURE = '[Account] Delete User Photo Failure',

  GET_CLIENT_ADDITIONS_REQUEST = '[Account] Get client additions Request',
  GET_CLIENT_ADDITIONS_SUCCESS = '[Account] Get client additions Success',
  GET_CLIENT_ADDITIONS_FAILURE = '[Account] Get client additions Failure',
}

const initialAccountState: IAccountState = {
  maxImageSizeBytes: 0,
  userProfilePhoto: null,
  clientAdditions: null,
};

const STORE_NAME = 'account';

@Injectable({ providedIn: 'root' })
export class AccountStore extends signalStore(
  withDevtools(STORE_NAME),
  withState(initialAccountState),
  withRequestStatus(),
  withStorageSync({
    key: STORE_NAME + 'State',
    select: ({ userProfilePhoto }) => ({ userProfilePhoto }),
  }),
  withComputed(store => ({
    userProfilePhotoUpdateError: computed<boolean>(() =>
      store.hasErrored(ELoadingEntity.UPDATE_PROFILE_PHOTO)
    ),
  })),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getImageMaxSize = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAccountActions.MAX_IMAGE_SIZE_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.MAX_IMAGE_SIZE, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getImageUploadMaxSize().pipe(
            map(response =>
              updateState(store, EAccountActions.MAX_IMAGE_SIZE_SUCCESS, ({ loadingProps, errorProps }) => ({
                maxImageSizeBytes: response.maxSize,
                ...withoutLoading(ELoadingEntity.MAX_IMAGE_SIZE, loadingProps),
                ...withoutError(ELoadingEntity.MAX_IMAGE_SIZE, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAccountActions.MAX_IMAGE_SIZE_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.MAX_IMAGE_SIZE, loadingProps),
                  ...withError(ELoadingEntity.MAX_IMAGE_SIZE, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getUserProfilePhoto = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAccountActions.USER_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getClientProfilePhoto().pipe(
            map(({ exists, url }) =>
              updateState(store, EAccountActions.USER_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                userProfilePhoto: exists && url ? url : null,
                ...withoutLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.USER_PROFILE_PHOTO, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAccountActions.USER_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.USER_PROFILE_PHOTO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    /**
     * 1. Get signed URL for updating client photo by providing size and content-type
     * 2. Upload photo to Minio using signed URL
     * 3. Confirm to backend that photo has been successfully updated
     * 4. Get updated photo from Minio
     */
    const updateUserProfilePhoto = rxMethod<{ imageProps: IImageUploadProps, image: Blob }>(
      pipe(
        tap(() =>
          updateState(store, EAccountActions.UPDATE_USER_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.UPDATE_PROFILE_PHOTO, loadingProps),
          }))
        ),
        switchMap(({ imageProps, image }) =>
          restApiService.requestUpdateClientProfilePhoto(imageProps).pipe(
            switchMap(({ url }) => restApiService.minioRequest(url, 'PUT', 'blob', image)),
            switchMap(() => restApiService.confirmClientProfilePhotoUpdated()),
            tap(() => {
              updateState(store, EAccountActions.UPDATE_USER_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.UPDATE_PROFILE_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.UPDATE_PROFILE_PHOTO, errorProps),
              }));
              getUserProfilePhoto();
            }),
            catchError((error) =>
              of(
                updateState(store, EAccountActions.UPDATE_USER_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.UPDATE_PROFILE_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.UPDATE_PROFILE_PHOTO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const deleteUserProfilePhoto = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAccountActions.DELETE_USER_PHOTO_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.deleteClientProfilePhoto().pipe(
            map(() => {
              updateState(store, EAccountActions.DELETE_USER_PHOTO_SUCCESS, ({ loadingProps, errorProps }) => ({
                ...withoutLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
                ...withoutError(ELoadingEntity.USER_PROFILE_PHOTO, errorProps),
              }));
              getUserProfilePhoto();
            }),
            catchError((error) =>
              of(
                updateState(store, EAccountActions.DELETE_USER_PHOTO_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.USER_PROFILE_PHOTO, loadingProps),
                  ...withError(ELoadingEntity.USER_PROFILE_PHOTO, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getClientAdditions = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EAccountActions.GET_CLIENT_ADDITIONS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.CLIENT_ADDITIONS, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getClientAdditions().pipe(
            map((clientAdditions) => {
              updateState(store, EAccountActions.GET_CLIENT_ADDITIONS_SUCCESS, ({ loadingProps, errorProps }) => ({
                clientAdditions,
                ...withoutLoading(ELoadingEntity.CLIENT_ADDITIONS, loadingProps),
                ...withoutError(ELoadingEntity.CLIENT_ADDITIONS, errorProps),
              }));
            }),
            catchError((error) =>
              of(
                updateState(store, EAccountActions.GET_CLIENT_ADDITIONS_FAILURE, ({ loadingProps, errorProps }) => ({
                  clientAdditions: null,
                  ...withoutLoading(ELoadingEntity.CLIENT_ADDITIONS, loadingProps),
                  ...withError(ELoadingEntity.CLIENT_ADDITIONS, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    return {
      getImageMaxSize,
      getUserProfilePhoto,
      updateUserProfilePhoto,
      deleteUserProfilePhoto,
      getClientAdditions,
    };
  })
) {}